import React, { createContext, useState, useContext, useEffect } from 'react'
import { supabase } from '../services/supabase'
import { trackEvent } from '../services/analytics'
import { getUTMParamsForTracking } from '../utils/utm'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let mounted = true

    // Force loading to false after 2 seconds max
    const forceLoadComplete = setTimeout(() => {
      if (mounted) {
        console.log('Force completing auth loading')
        setLoading(false)
      }
    }, 2000)

    // Simple auth check
    const initAuth = async () => {
      try {
        // Quick check for existing session
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (!mounted) return

        if (error) {
          console.warn('Auth session error:', error)
        } else if (session?.user) {
          setUser(session.user)
          setProfile({ full_name: '', username: '' }) // Simple default profile
          // Analytics removed for debugging
        }
      } catch (error) {
        console.warn('Auth init error:', error)
      }
      
      if (mounted) {
        setLoading(false)
        clearTimeout(forceLoadComplete)
      }
    }

    initAuth()

    // Simple auth listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return

      console.log('🔄 Auth state change:', { event, hasSession: !!session, userId: session?.user?.id })

      setUser(session?.user ?? null)
      if (session?.user) {
        console.log('✅ User authenticated:', session.user.email)
        setProfile({ full_name: '', username: '' })
        if (event === 'SIGNED_IN') {
          try {
            trackEvent('funnel_auth_completed', {
              source: 'main_funnel',
              method: session.user.app_metadata?.provider || 'unknown',
            })
          } catch (e) { /* non-blocking */ }
        }
      } else {
        console.log('❌ User not authenticated')
        setProfile(null)
      }
    })

    return () => {
      mounted = false
      clearTimeout(forceLoadComplete)
      subscription?.unsubscribe()
    }
  }, [])

  const signUp = async (email, password, metadata = {}) => {
    console.log('🚀 AuthContext signUp called with:', { email, metadata })
    try {
      setError(null)
      console.log('📡 Calling Supabase auth.signUp...')
      
      // First, let's check if we can connect to Supabase
      const { data: { session: testSession }, error: testError } = await supabase.auth.getSession()
      console.log('🔍 Test session check:', { hasSession: !!testSession, testError })
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { 
          data: metadata,
          emailRedirectTo: null // Disable email verification
        }
      })
      
      // If signup was successful but no session, try to create profile manually
      if (data?.user && !data.session && !error) {
        console.log('🔧 User created but no session, attempting to create profile manually...')
        try {
          const { error: profileError } = await supabase
            .from('profiles')
            .insert([{
              id: data.user.id,
              full_name: metadata?.full_name || '',
              username: metadata?.username || ''
            }])
          
          if (profileError) {
            console.warn('⚠️ Failed to create profile manually:', profileError)
            // Don't fail the signup for this
          } else {
            console.log('✅ Profile created manually')
          }
        } catch (profileException) {
          console.warn('⚠️ Exception creating profile manually:', profileException)
          // Don't fail the signup for this
        }
      }
      
      console.log('📊 Supabase signUp response:', { 
        hasData: !!data, 
        hasUser: !!data?.user, 
        hasSession: !!data?.session,
        error: error?.message || error,
        errorCode: error?.status,
        errorDetails: error?.details
      })
      
      if (error) {
        console.error('❌ Supabase signUp error details:', {
          message: error.message,
          status: error.status,
          details: error.details,
          hint: error.hint,
          code: error.code
        })
        
        // Provide more specific error messages
        let userFriendlyMessage = error.message
        if (error.message.includes('Database error saving new user')) {
          userFriendlyMessage = 'There was an issue creating your account. This might be due to a database configuration problem. Please try again or contact support.'
        } else if (error.message.includes('User already registered')) {
          userFriendlyMessage = 'An account with this email already exists. Please try signing in instead.'
        } else if (error.message.includes('Password should be at least')) {
          userFriendlyMessage = 'Password must be at least 6 characters long.'
        }
        
        setError(userFriendlyMessage)
        return { data: null, error: { ...error, message: userFriendlyMessage } }
      }
      
      const name = metadata?.full_name || metadata?.name || (email ? email.split('@')[0] : '')
      console.log('✅ signUp successful, returning data')
      
      // Track signup event with UTM parameters
      const utmParams = getUTMParamsForTracking()
      trackEvent('User Signed Up', {
        email,
        name,
        has_session: !!data?.session,
        signup_method: 'email',
        ...utmParams
      })
      
      return { data, error: null }
    } catch (error) {
      console.error('💥 signUp exception:', error)
      setError(error.message || 'An unexpected error occurred during signup')
      return { data: null, error }
    }
  }

  const signIn = async (email, password) => {
    try {
      setError(null)
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      if (error) throw error
      // Analytics removed for debugging
      return { data, error: null }
    } catch (error) {
      setError(error.message)
      return { data: null, error }
    }
  }

  const signInWithGoogle = async (redirectAfterAuth = '/') => {
    try {
      setError(null)
      console.log('🔐 Starting Google OAuth sign-in...')
      
      // Build a callback URL that also carries the intended in-app destination.
      // This avoids losing state across the OAuth full-page redirect.
      let nextPath = redirectAfterAuth
      try {
        const parsed = new URL(redirectAfterAuth)
        if (parsed.origin === window.location.origin) {
          nextPath = `${parsed.pathname}${parsed.search}${parsed.hash}`
        }
      } catch {
        // redirectAfterAuth is likely a path already
      }
      if (typeof nextPath !== 'string' || !nextPath.startsWith('/')) {
        nextPath = '/dashboard'
      }

      const redirectUrl = new URL('/auth/callback', window.location.origin)
      redirectUrl.searchParams.set('next', nextPath)
      console.log('🔗 OAuth redirect URL:', redirectUrl.toString())
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl.toString(),
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      })
      
      if (error) {
        console.error('❌ Google OAuth error:', error)
        throw error
      }
      
      console.log('✅ Google OAuth initiated:', data)
      
      // Track signup/signin event with UTM parameters
      const utmParams = getUTMParamsForTracking()
      trackEvent('OAuth Started', {
        provider: 'google',
        redirect_to: nextPath,
        ...utmParams
      })
      
      return { data, error: null }
    } catch (error) {
      console.error('💥 Google OAuth exception:', error)
      setError(error.message || 'Failed to sign in with Google')
      return { data: null, error }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      setUser(null)
      setProfile(null)
      // Analytics removed for debugging
      return { error: null }
    } catch (error) {
      setError(error.message)
      return { error }
    }
  }

  const resetPassword = async (email) => {
    try {
      setError(null)
      console.log('🔐 Attempting password reset for:', email)
      
      // Always use the deployed URL for password reset
      const deployedUrl = 'https://www.valycode.com';
      
      console.log('🔗 Password reset redirect URL:', deployedUrl);
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${deployedUrl}/reset-password`
      })
      
      if (error) {
        console.error('❌ Password reset error:', error)
        throw error
      }
      
      console.log('✅ Password reset email sent successfully')
      return { error: null }
    } catch (error) {
      console.error('💥 Password reset exception:', error)
      setError(error.message)
      return { error }
    }
  }

  const updateProfile = async (updates) => {
    try {
      setError(null)
      if (!user) throw new Error('No user logged in')
      
      // Just update local state for now
      const updatedProfile = { ...profile, ...updates }
      setProfile(updatedProfile)
      return { data: updatedProfile, error: null }
    } catch (error) {
      setError(error.message)
      return { data: null, error }
    }
  }

  const value = {
    user,
    profile,
    loading,
    error,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    updateProfile,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext